import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Plus, Trash2, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubUsersPage = () => {
  const { user } = useAuth();
  const [subUsers, setSubUsers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newSubUser, setNewSubUser] = useState({
    email: '',
    full_name: '',
    permissions: []
  });

  const availablePermissions = [
    { id: 'manage_gigs', label: 'Manage Gigs' },
    { id: 'manage_projects', label: 'Manage Projects' },
    { id: 'manage_contracts', label: 'Manage Contracts' },
    { id: 'manage_payments', label: 'Manage Payments' },
    { id: 'view_analytics', label: 'View Analytics' },
    { id: 'manage_team', label: 'Manage Team' }
  ];

  useEffect(() => {
    fetchSubUsers();
  }, []);

  const fetchSubUsers = async () => {
    try {
      const res = await axios.get(`${API}/sub-users`);
      setSubUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch sub-users', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/sub-users`, newSubUser);
      toast.success('Sub-user added successfully!');
      setShowAddDialog(false);
      setNewSubUser({ email: '', full_name: '', permissions: [] });
      fetchSubUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sub-user');
    }
  };

  const handleDeleteSubUser = async (subUserId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      await axios.delete(`${API}/sub-users/${subUserId}`);
      toast.success('Sub-user removed successfully');
      fetchSubUsers();
    } catch (error) {
      toast.error('Failed to remove sub-user');
    }
  };

  const handleTogglePermission = (permissionId) => {
    setNewSubUser(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2">Team Management</h1>
          <p className="text-muted-foreground">Add team members and manage their permissions</p>
        </div>

        <div className="mb-6">
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-secondary text-secondary-foreground" data-testid="add-sub-user-button">
                <Plus className="w-4 h-4 mr-2" /> Add Team Member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add Team Member</DialogTitle>
                <DialogDescription>Create a sub-user account with specific permissions</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddSubUser} className="space-y-4" data-testid="add-sub-user-form">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={newSubUser.full_name}
                    onChange={(e) => setNewSubUser({ ...newSubUser, full_name: e.target.value })}
                    required
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newSubUser.email}
                    onChange={(e) => setNewSubUser({ ...newSubUser, email: e.target.value })}
                    required
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label className="mb-3 block">Permissions</Label>
                  <div className="space-y-2">
                    {availablePermissions.map((perm) => (
                      <div key={perm.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={perm.id}
                          checked={newSubUser.permissions.includes(perm.id)}
                          onCheckedChange={() => handleTogglePermission(perm.id)}
                        />
                        <label htmlFor={perm.id} className="text-sm font-medium cursor-pointer">
                          {perm.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <Button type="submit" className="w-full">Add Team Member</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : subUsers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No team members yet. Add your first team member to collaborate!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {subUsers.map((subUser) => (
              <Card key={subUser.id} data-testid={`sub-user-card-${subUser.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{subUser.full_name}</CardTitle>
                      <CardDescription>{subUser.email}</CardDescription>
                    </div>
                    <Badge variant={subUser.active ? 'default' : 'secondary'}>
                      {subUser.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium mb-2">Permissions:</div>
                      <div className="flex flex-wrap gap-2">
                        {subUser.permissions.map((perm) => (
                          <Badge key={perm} variant="secondary" className="text-xs">
                            {availablePermissions.find(p => p.id === perm)?.label || perm}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="pt-3 flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteSubUser(subUser.id)}
                        data-testid={`delete-sub-user-${subUser.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Remove
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubUsersPage;